/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { useContext, useMemo } from 'react';
import { Input, Label } from 'reactstrap';
import { useSelector } from 'react-redux';
import { updateFn } from '@g17eco/molecules/form';
import { RootState, useAppSelector } from '@reducers/index';
import { SurveyContext } from '@features/survey-container/SurveyContainer';
import { InitiativeMin } from '@g17eco/types/initiative';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import { ConfigFormData, ErrorState } from '../types';
import { getCheckOptions, getFormattedSurveyDate } from '@utils/survey';
import { SurveyQuestionCollapseForm } from './SurveyQuestionForm';
import { OnChangeReportingProps, SurveyReportingSettings } from './SurveyReportingSettings';
import { FeaturePermissions } from '@services/permissions/FeaturePermissions';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { SURVEY } from '@constants/terminology';
import { ERRORS } from '../utils';
import { getRootConfig } from '@selectors/globalData';

const REPORT_NAME_TOOLTIP =
  'Chosen name for the report. By default this will be the subsidiary name followed by the selected period, month and year';

const getSurveyName = (
  surveyData?: Partial<Pick<SurveyActionData, 'name' | 'period' | 'effectiveDate'>>,
  initiative?: Pick<InitiativeMin, 'name'>
) => {
  if (surveyData?.name) {
    return surveyData.name;
  }
  if (!initiative?.name) {
    return `Unnamed ${SURVEY.SINGULAR}`;
  }
  if (surveyData?.effectiveDate && surveyData?.period) {
    return `${initiative.name} ${getFormattedSurveyDate({
      effectiveDate: surveyData.effectiveDate,
      period: surveyData.period,
    })}`;
  }
  return initiative.name;
};

interface SurveySettingsProps {
  updateForm: updateFn;
  form: ConfigFormData;
  errorState: ErrorState;
}

export const SurveySettings = (props: SurveySettingsProps) => {
  const { updateForm, form, errorState } = props;
  const { surveyData, disabled } = useContext(SurveyContext);

  const isDisabled = Boolean(surveyData.completedDate) || disabled;

  const initiativeState = useSelector((state: RootState) => state.initiative);
  const initiative = initiativeState.data;

  const rootConfig = useAppSelector(getRootConfig);
  const canAccessVerification = FeaturePermissions.canAccessVerification(rootConfig);
  const isCombinedReport = useMemo(() => surveyData.type === SurveyType.Aggregation, [surveyData]);

  const checkOptions = getCheckOptions(canAccessVerification);

  const handleChange = ({ option, code }: OnChangeReportingProps) => {
    return updateForm({ code, value: option?.value });
  };

  return (
    <>
      <div className='mt-4'>
        <Label>
          {SURVEY.CAPITALIZED_SINGULAR} name (optional)
          <SimpleTooltip text={REPORT_NAME_TOOLTIP}>
            <i className='fal fa-circle-info text-ThemeIconSecondary ml-2' />
          </SimpleTooltip>
        </Label>
        <Input
          disabled={isDisabled}
          placeholder={getSurveyName(surveyData, initiative)}
          onChange={(el) => updateForm({ code: 'name', value: el.target.value })}
          defaultValue={form.name}
          data-testid='survey-name-input'
        />
      </div>

      <SurveyReportingSettings
        formDisabled={errorState.error === ERRORS.REPORT_LIMIT}
        isDisabled={isDisabled}
        form={form}
        onChange={handleChange}
        errorState={errorState}
      />

      {isCombinedReport ? null : (
        <SurveyQuestionCollapseForm
          disabled={errorState.error === ERRORS.REPORT_LIMIT}
          isCheckboxDisabled={isDisabled}
          checkOptions={checkOptions}
          form={{ ...form, verificationRequired: canAccessVerification && form.verificationRequired }}
          updateForm={updateForm}
        />
      )}
    </>
  );
};
